/**
 * LeadStaffMenu — mobile menu for role=crew_leader, supervisor
 * (and by inheritance any staff who logs in with those roles).
 *
 * S4 Addendum: renders every section listed by re-mounting existing
 * dashboard components in a single-column, large-touch-target layout,
 * driven by a bottom nav. All role-based visibility rules already
 * baked into the individual components carry over untouched.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Home, ClipboardList, Users, MessageSquare,
  Camera, Package, Trophy, CalendarClock, FileText, History,
  Clock, Bell, Sparkles, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileShell, BottomNavButton } from './MobileShell';

// Existing components — every one already handles its own role gating.
import PickupQueueCard from '../PickupQueueCard';
import FlagUnitCard from '../FlagUnitCard';
import QRScanner from '../QRScanner';
import SupervisorSpecialtyQueue from '../SupervisorSpecialtyQueue';
import AttendanceTab from '../AttendanceTab';
import CommercialCleanersBoard from './CommercialCleanersBoard';
import ComplianceTrophy from '../ComplianceTrophy';
import UpdatesNoticesPanel from '../updates/UpdatesNoticesPanel';
import { HierarchyAssignments } from '../staff/HierarchyAssignments';
import { CrewLeaderboard } from '../staff/CrewLeaderboard';
import { ServiceHistorySummary } from '../staff/ServiceHistorySummary';
import { SupervisorRegionalAssignmentCard } from '../staff/SupervisorRegionalAssignmentCard';
import { SupervisorProgressBars } from '../staff/SupervisorProgressBars';
import ServiceSummaryWidget from '../staff/ServiceSummaryWidget';
import StaffReportsTab from '../staff/StaffReportsTab';
import StaffChatTab from '../staff/StaffChatTab';
import { OrderedJobStepperCard } from '../staff/OrderedJobStepperCard';
import { MyDailyAssignments } from '../staff/MyDailyAssignments';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

// The full mobile section catalog. Each entry is a self-contained tile
// that mounts existing web components in a single-column layout.
function TileSectionHeader({ icon: Icon, label, right }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: GOLD }} />
        <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: GOLD }}>
          {label}
        </p>
      </div>
      {right}
    </div>
  );
}

export function LeadStaffMenu({ user }) {
  const location = useLocation();
  const [active, setActive] = useState('today');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Iter-143 · FIX 4 — honour ?tab= deep-links so redirects from
  // /cleaners-command-center land on the Commercial Cleaners tab.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const t = params.get('tab');
    if (t && ['today', 'scan', 'team', 'cleaners', 'notices', 'more'].includes(t)) {
      setActive(t);
    }
  }, [location.search]);

  const role = (user?.role || '').toLowerCase();
  const isSupervisor = role === 'supervisor';
  const isCrewLeader = role === 'crew_leader';

  const handleQR = async (data) => {
    setScannerOpen(false);
    if (!data) return;
    try {
      const r = await fetch(`${API}/api/qr-scan/scan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: data, source: 'staff-app' }),
      });
      if (r.ok) toast.success('Scan logged');
      else toast.error('QR not recognized');
    } catch { toast.error('Scan failed'); }
  };

  const sendChat = async () => {
    if (!newMessage.trim()) return;
    try {
      const r = await fetch(`${API}/api/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage.trim() }),
      });
      if (r.ok) {
        setChatMessages((p) => [...p, { content: newMessage.trim(), sender_id: user?.user_id, created_at: new Date().toISOString() }]);
        setNewMessage('');
      }
    } catch { /* noop */ }
  };

  const bottomNav = (
    <>
      <BottomNavButton icon={Home}          label="Today"     active={active === 'today'}     onClick={() => setActive('today')}     testId="staff-app-nav-today" />
      <BottomNavButton icon={Camera}        label="Scan"      active={active === 'scan'}      onClick={() => setActive('scan')}      testId="staff-app-nav-scan" />
      <BottomNavButton icon={Users}         label="Team"      active={active === 'team'}      onClick={() => setActive('team')}      testId="staff-app-nav-team" />
      <BottomNavButton icon={Sparkles}      label="Cleaners"  active={active === 'cleaners'}  onClick={() => setActive('cleaners')}  testId="staff-app-nav-cleaners" />
      <BottomNavButton icon={MessageSquare} label="More"      active={active === 'more'}      onClick={() => setActive('more')}      testId="staff-app-nav-more" />
    </>
  );

  const title = isSupervisor ? 'Supervisor · Command' : isCrewLeader ? 'Crew Leader · Command' : 'Staff · Command';

  return (
    <MobileShell title={title} subtitle={user?.name || user?.email} bottomNav={bottomNav} testId="staff-app-lead">
      <div className="flex flex-wrap gap-4 py-2"><a href="/shift-briefings" className="text-sm text-[#C9A962] underline" data-testid="lead-briefings-link">NECS Standard briefings</a><a href="/closeout-attention" className="text-sm text-amber-300 underline" data-testid="lead-attention-link">Overdue & returned work</a></div>
      <div className="flex flex-wrap gap-4 py-2"><a href="/operation-records" className="text-sm text-[#C9A962] underline" data-testid="lead-closeout-link">Property closeout reviews</a><a href="/staff-rewards" className="text-sm text-[#C9A962] underline" data-testid="lead-rewards-link">Monthly progress & rewards</a></div>
      {active === 'today' && (
        <>
          <section data-testid="staff-app-lead-today">
            <TileSectionHeader icon={Home} label="Today · Active Shift" />
            <MyDailyAssignments />
          </section>
          <section>
            <TileSectionHeader icon={ClipboardList} label="Pending Actions" />
            <PickupQueueCard />
            <div className="mt-3"><FlagUnitCard /></div>
          </section>
          <section>
            <TileSectionHeader icon={Sparkles} label="Specialty Queue" />
            {['supervisor', 'crew_leader'].includes(role) && <SupervisorSpecialtyQueue user={user} />}
          </section>
          <section>
            <TileSectionHeader icon={Trophy} label="Compliance & Trophy Board" />
            <ComplianceTrophy role={user?.role} roleType={user?.role_type} primaryService={user?.primary_service} />
          </section>
          <section>
            <TileSectionHeader icon={Sparkles} label="Live Progress" />
            <SupervisorProgressBars user={user} />
          </section>
          <section>
            <TileSectionHeader icon={ClipboardList} label="Ordered Job Steps" />
            <OrderedJobStepperCard />
          </section>
        </>
      )}

      {active === 'scan' && (
        <>
          <section data-testid="staff-app-lead-scan">
            <TileSectionHeader icon={Camera} label="Scan QR · Trash Pickup" />
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              className="w-full min-h-[64px] rounded-2xl font-black uppercase tracking-widest text-base"
              style={{ background: GOLD, color: '#0a0a0a' }}
              data-testid="staff-app-lead-scan-open"
            >
              Tap to Scan QR
            </button>
            <p className="text-[11px] text-zinc-500 mt-3">
              Property / unit / badge QR — routes to the same endpoint the web app uses.
            </p>
          </section>
          <section>
            <TileSectionHeader icon={Package} label="Pickup Queue" />
            <PickupQueueCard />
          </section>
        </>
      )}

      {active === 'team' && (
        <>
          <section data-testid="staff-app-lead-team">
            <TileSectionHeader icon={Users} label="Team Assignments" />
            <SupervisorRegionalAssignmentCard user={user} />
          </section>
          <section>
            <TileSectionHeader icon={ClipboardList} label="Hierarchy Board" />
            <HierarchyAssignments user={user} />
          </section>
          <section>
            <TileSectionHeader icon={Award} label="Crew Leaderboard" />
            <CrewLeaderboard />
          </section>
          {/* Iter-143 · FIX 2/3 — Uber Trash Ops section removed. Commercial
              Cleaners now lives in its own dedicated bottom-nav tab. */}
        </>
      )}

      {active === 'cleaners' && (
        <section data-testid="staff-app-lead-cleaners">
          <TileSectionHeader icon={Sparkles} label="Commercial Cleaners HQ" />
          <CommercialCleanersBoard testId="staff-app-lead-cleaners-board" />
        </section>
      )}

      {active === 'more' && (
        <>
          <section data-testid="staff-app-lead-more">
            <TileSectionHeader icon={Bell} label="Updates & Notices" />
            <UpdatesNoticesPanel testIdPrefix="staff-app-updates" />
          </section>
          <section>
            <TileSectionHeader icon={History} label="Service History Summary" />
            <ServiceHistorySummary user={user} />
          </section>
          <section>
            <TileSectionHeader icon={FileText} label="My Reports" />
            <StaffReportsTab user={user} myDailyReports={[]} myViolations={[]} properties={[]} regions={[]} vehicles={[]} />
          </section>
          <section>
            <TileSectionHeader icon={Clock} label="Attendance · Clock-in / Clock-out" />
            <AttendanceTab user={user} />
          </section>
          <section>
            <TileSectionHeader icon={CalendarClock} label="My Schedule · Shift Summary" />
            <ServiceSummaryWidget properties={[]} />
          </section>
          <section>
            <TileSectionHeader icon={MessageSquare} label="Messages" />
            <StaffChatTab
              user={user}
              chatMessages={chatMessages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              onSendMessage={sendChat}
            />
          </section>
        </>
      )}

      <QRScanner
        open={scannerOpen}
        onOpenChange={(v) => { if (!v) setScannerOpen(false); }}
        onScan={handleQR}
        title="Scan QR"
        description="Property, unit, or badge — same endpoint as the web app."
        dataTestId="staff-app-lead-qr-scanner"
      />
    </MobileShell>
  );
}

// Fallback icon (used above) — kept inline to avoid a new file. Lucide's
// ShieldStar isn't in the pack, so we alias it to a familiar mark.
// (No longer used — kept as a placeholder for future spec extensions.)

export default LeadStaffMenu;

